import { observable, computed, makeObservable } from 'mobx'

import { isBefore } from 'date-fns'
import { Model } from 'infra/model'
import { CouponDTO, CouponTypeDTO } from 'billing/dtos/coupon'
import { maybeParseDate } from 'utils/date-util'
import { Id } from 'types/definitions'
import once from 'lodash/once'
import { formatMoney } from 'utils/money-util'

export default class Coupon extends Model<CouponDTO, Coupon> {
  readonly id = Id.none
  readonly workspaceId = Id.none

  @observable code = ''
  @observable friendlyName = ''
  @observable description = ''
  @observable maxTotalRedemptions = 0
  @observable dateExpires: Date | null = null
  @observable duration: CouponDuration = 'ONCE'
  @observable couponType: CouponType = { type: 'REGULAR' }
  @observable dateCreated: Date = new Date(0)
  @observable dateModified: Date = new Date(0)
  @observable offType: OffType = { type: 'AMOUNT_OFF', amountOff: 0 }
  @observable maxOff: MaxOff | null = null

  constructor() {
    super()

    makeObservable(this)
  }

  static get none() {
    return lazyNone()
  }

  @computed
  get expired() {
    return this.dateExpires ? isBefore(this.dateExpires, new Date()) : false
  }

  @computed
  get discount() {
    return this.offType.type === 'AMOUNT_OFF'
      ? `${formatMoney(this.offType.amountOff)}-OFF`
      : `${this.offType.percentOff}%-OFF`
  }

  parse(json: CouponDTO): Partial<Coupon> {
    return {
      id: json.id,
      code: json.code,
      workspaceId: json.workspace_id,
      friendlyName: json.friendly_name,
      description: json.description,
      maxTotalRedemptions: json.max_total_redemptions,
      dateExpires: maybeParseDate(json.date_expires),
      duration: json.duration,
      couponType: parseCouponType(json.coupon_type),
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      offType:
        json.off_type.type === 'AMOUNT_OFF'
          ? {
              type: json.off_type.type,
              amountOff: json.off_type.amount_off,
            }
          : {
              type: json.off_type.type,
              percentOff: json.off_type.percent_off,
            },
      maxOff:
        json.max_off &&
        (json.max_off.type === 'MAX_PERCENT_OFF'
          ? {
              type: 'MAX_PERCENT_OFF',
              percentOff: json.max_off.percent_off,
            }
          : {
              type: 'MAX_AMOUNT_OFF',
              amountOff: json.max_off.amount_off,
            }),
    }
  }
}

const lazyNone = once(() => new Coupon().set({ id: 'none' }))

function parseCouponType(dto: CouponTypeDTO): CouponType {
  switch (dto.type) {
    case 'REGULAR': {
      return {
        type: 'REGULAR',
      }
    }
    case 'REFERRAL_CODE': {
      return {
        type: 'REFERRAL_CODE',
        referrerUserId: dto.referrer_user_id,
      }
    }
    case 'CUSTOM_DISCOUNT': {
      return {
        type: 'CUSTOM_DISCOUNT',
        createdByUserId: dto.created_by_user_id,
      }
    }
  }
}

/**
 * Plain old coupon.
 */
export interface RegularCouponType {
  type: 'REGULAR'
}
/**
 * In case we are feeling extra generous, we can add a custom discount
 * to specific users.
 */
export interface CustomDiscountCouponType {
  type: 'CUSTOM_DISCOUNT'
  createdByUserId: string
}
/**
 * Used by the platform API to set up referral coupons for users.
 */
export interface ReferralCouponType {
  type: 'REFERRAL_CODE'
  referrerUserId: string
}
/**
 * Coupon Type Enum.
 */
export type CouponType =
  | RegularCouponType
  | ReferralCouponType
  | CustomDiscountCouponType

/**
 * How many times can a single user redeem this coupon?
 * Note that a single user can at any time only have
 * a single unredeemed instance of the coupon at any time,
 * regardless of this value.
 */
export type CouponDuration = 'ONCE' | 'FOREVER'

/**
 * A Coupon that provides a fixed discount.
 */
export interface AmountOffType {
  type: 'AMOUNT_OFF'
  amountOff: number
}
/**
 * A Coupon that provides a percent-based discount.
 */
export interface PercentOffType {
  type: 'PERCENT_OFF'
  percentOff: number
}
/**
 * The maximum amount discount on PercentType coupon
 */
export interface MaxAmountOffType {
  type: 'MAX_AMOUNT_OFF'
  amountOff: number
}
/**
 * The maximum percent off discount on AmountType coupon
 */
export interface MaxPercentOffType {
  type: 'MAX_PERCENT_OFF'
  percentOff: number
}
/**
 * Off Type union.
 */
export declare type OffType = AmountOffType | PercentOffType
export declare type MaxOff = MaxAmountOffType | MaxPercentOffType
